import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import Typography from '@mui/material/Typography';
import { AccountData, PropelUserModel } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { useLanguage } from '../../common/GeneralUtils';
import {
  getActiveLocations,
  getEffectivePageLocationId,
  getLocationName,
  isMultiLocation,
} from '../../common/filter/locationGrouping';

export const getUserFullName = (user: PropelUserModel) => `${user.first_name} ${user.last_name}`;
export const getUserFullNameFromUserId = (users: PropelUserModel[], userId: string) => {
  const user = users.find((x) => x.user_id === userId);
  if (user) {
    return getUserFullName(user);
  }
  return '';
};

type ListedUser = PropelUserModel & { fullName: string };

type UsersGroup = {
  locationId: string | null;
  name: string;
  users: ListedUser[];
};

const WITHOUT_LOCATION_KEY = '__no_location__';
const POPOVER_SHADOW = '0 8px 24px rgba(31, 26, 23, 0.12)';
const POPOVER_MIN_WIDTH = 240;
const CHILD_INDENT = '28px';

export const UsersMultiSelect = ({
  users,
  selectedUsersIds,
  setSelectedUsersIds,
  size = 'medium',
  minWidth = 195,
  label,
  helperText,
  locationId,
  visibleLocationIds,
  showLocationWarning,
}: {
  users: PropelUserModel[];
  selectedUsersIds: string[];
  setSelectedUsersIds: (selected: string[]) => void;
  size?: 'small' | 'medium';
  minWidth?: number;
  label?: string;
  helperText?: string;
  locationId?: string | null;
  visibleLocationIds?: string[] | null;
  showLocationWarning?: boolean;
}) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const language = useLanguage();
  const account = authState.account as unknown as AccountData;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const multiLocation = isMultiLocation(account);
  const activeLocations = getActiveLocations(account);
  const knownUsers = users ?? [];
  const warningEnabled = multiLocation && !!locationId && (showLocationWarning ?? true);

  const getUserLocationId = (user: PropelUserModel) => getEffectivePageLocationId(user.location_id, account);
  const getLocationNameById = (id: string | null) => {
    const location = activeLocations.find((activeLocation) => activeLocation.id === id);
    return location ? getLocationName(location, language) : '';
  };
  const isCrossLocation = (user: PropelUserModel) => {
    const userLocationId = getUserLocationId(user);
    return warningEnabled && userLocationId !== null && userLocationId !== locationId;
  };

  const listedUsers: ListedUser[] = knownUsers
    .filter((user) => user.enabled !== false || selectedUsersIds.includes(user.user_id))
    .filter((user) => {
      const userLocationId = getUserLocationId(user);
      return (
        !multiLocation || !visibleLocationIds || userLocationId === null || visibleLocationIds.includes(userLocationId)
      );
    })
    .map((user) => ({ ...user, fullName: getUserFullName(user) }))
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  const field = (
    <FormControl
      variant="outlined"
      sx={{
        minWidth: `${minWidth}px`,
      }}>
      <InputLabel size={size === 'small' ? 'small' : 'normal'} id="select-label">
        <Typography> {isEmpty(selectedUsersIds) ? label ?? t('calendar.event_popper.employees') : ''}</Typography>
      </InputLabel>

      <Select
        size={size}
        labelId="select-label"
        multiple
        value={selectedUsersIds}
        open={multiLocation ? false : undefined}
        onOpen={multiLocation ? (event) => setAnchorEl(event.currentTarget as HTMLElement) : undefined}
        onChange={(e) => {
          // @ts-ignore
          setSelectedUsersIds(e.target.value);
        }}
        renderValue={(selected) => (
          <Stack gap={1} direction="row" flexWrap="wrap">
            {selected.map((userId) => {
              const user = knownUsers.find((knownUser) => knownUser.user_id === userId);
              const crossLocation = user ? isCrossLocation(user) : false;
              return (
                <Chip
                  key={userId}
                  label={
                    user && crossLocation
                      ? `${getUserFullName(user)} · ${getLocationNameById(getUserLocationId(user))}`
                      : getUserFullNameFromUserId(knownUsers, userId)
                  }
                  sx={
                    crossLocation
                      ? {
                          bgcolor: 'secondary.light',
                          color: 'secondary.dark',
                          '& .MuiChip-deleteIcon': { color: 'secondary.dark' },
                        }
                      : undefined
                  }
                  onDelete={() => setSelectedUsersIds(selectedUsersIds.filter((id) => id !== userId))}
                  deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()} />}
                />
              );
            })}
          </Stack>
        )}>
        {multiLocation
          ? null
          : listedUsers.map((user) => (
              <MenuItem key={`MenuItem-${user.user_id}`} value={user.user_id} sx={{ justifyContent: 'space-between' }}>
                {user.fullName}
                {selectedUsersIds.includes(user.user_id) ? <CheckIcon color="primary" /> : null}
              </MenuItem>
            ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );

  if (!multiLocation) {
    return field;
  }

  const toggleUsers = (userIds: string[], checked: boolean) => {
    setSelectedUsersIds(
      checked
        ? [...new Set([...selectedUsersIds, ...userIds])]
        : selectedUsersIds.filter((id) => !userIds.includes(id)),
    );
  };

  const listedUserIds = listedUsers.map((user) => user.user_id);
  const allListedSelected =
    listedUserIds.length > 0 && listedUserIds.every((userId) => selectedUsersIds.includes(userId));

  const groups: UsersGroup[] = activeLocations
    .map((location) => ({
      locationId: location.id,
      name: getLocationName(location, language),
      users: listedUsers.filter((user) => getUserLocationId(user) === location.id),
    }))
    .filter((group) => group.users.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
  const usersWithoutLocation = listedUsers.filter((user) => getUserLocationId(user) === null);
  if (usersWithoutLocation.length > 0) {
    groups.push({ locationId: null, name: t('users.location_floating'), users: usersWithoutLocation });
  }

  const primaryGroup = locationId ? groups.find((group) => group.locationId === locationId) : undefined;
  const otherGroups = groups.filter((group) => group !== primaryGroup);

  const renderGroup = (group: UsersGroup) => {
    const groupUserIds = group.users.map((user) => user.user_id);
    const selectedCount = groupUserIds.filter((userId) => selectedUsersIds.includes(userId)).length;
    return (
      <React.Fragment key={group.locationId ?? WITHOUT_LOCATION_KEY}>
        <FormControlLabel
          sx={{ mx: 0, paddingInline: '8px' }}
          label={
            <Grid container alignItems="center" flexWrap="nowrap" gap={0.5}>
              <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography sx={{ fontWeight: 600 }}>{group.name}</Typography>
            </Grid>
          }
          control={
            <Checkbox
              size="small"
              checked={selectedCount === groupUserIds.length}
              indeterminate={selectedCount > 0 && selectedCount < groupUserIds.length}
              onChange={(event) => toggleUsers(groupUserIds, event.target.checked)}
            />
          }
        />
        {group.users.map((user) => (
          <FormControlLabel
            key={user.user_id}
            sx={{ mx: 0, paddingInlineStart: CHILD_INDENT, paddingInlineEnd: '8px' }}
            label={user.fullName}
            control={
              <Checkbox
                size="small"
                checked={selectedUsersIds.includes(user.user_id)}
                onChange={(event) => toggleUsers([user.user_id], event.target.checked)}
              />
            }
          />
        ))}
      </React.Fragment>
    );
  };

  const activitySingular = account.activity_label_singular?.[language] ?? t('passes.activity_fallback_singular');

  return (
    <Grid container flexDirection="column" gap={1} sx={{ width: 'auto' }}>
      {field}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: anchorEl?.clientWidth,
              minWidth: POPOVER_MIN_WIDTH,
              maxHeight: '60vh',
              boxShadow: POPOVER_SHADOW,
            },
          },
        }}>
        <Grid container flexDirection="column" flexWrap="nowrap" role="group">
          {listedUsers.length > 0 && (
            <>
              <Grid container justifyContent="flex-end" sx={{ px: 1, py: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => toggleUsers(listedUserIds, !allListedSelected)}
                  sx={{ textTransform: 'none', py: 0, minWidth: 0, color: 'text.secondary' }}>
                  <Typography variant="caption">
                    {allListedSelected ? t('usersPicker.deselect_all') : t('usersPicker.select_all')}
                  </Typography>
                </Button>
              </Grid>
              <Divider />
            </>
          )}
          {primaryGroup && renderGroup(primaryGroup)}
          {primaryGroup && otherGroups.length > 0 && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                {t('usersPicker.other_locations')}
              </Typography>
            </>
          )}
          {otherGroups.map(renderGroup)}
          {visibleLocationIds && (
            <>
              <Divider />
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
                {t('usersPicker.filtered_by_calendar')}
              </Typography>
            </>
          )}
        </Grid>
      </Popover>
      {knownUsers
        .filter((user) => selectedUsersIds.includes(user.user_id) && isCrossLocation(user))
        .map((user) => (
          <Alert key={user.user_id} severity="warning">
            {t('usersPicker.cross_location_warning', {
              name: getUserFullName(user),
              employeeLocation: getLocationNameById(getUserLocationId(user)),
              activitySingular,
              location: getLocationNameById(locationId ?? null),
            })}
          </Alert>
        ))}
    </Grid>
  );
};
